// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import {
  getActivePanelId,
  getCertInfo,
  getCertRequestData,
} from 'state/selectors';
import type { Dispatch, CertInfo, CertRequestData } from 'state/types';
import localizedStrings from 'util/localizedStrings';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import StaticTextField from 'view/Forms/StaticTextField';
import {
  deleteCertRequest,
  downloadCertRequest,
  getCertStatus,
} from 'state/commands/certCommands';
import { getCertStatusStyle, getCertStatusText } from 'util/certUtils';
import {
  CREATE_CERT_REQUEST_PANEL_ID,
  CERT_UPLOAD_PANEL_ID,
} from 'util/constants';
import CreateCertRequestDialog from './CreateCertRequestDialog';
import CertUploadDialog from './CertUploadDialog';
import { actions } from 'portfolio-shared/state/reducers/dialogPanelReducer';
const { showDialogPanel } = actions;

type Props = {
  activePanelId: string,
  certInfo?: CertInfo,
  certRequest?: CertRequestData,
  deleteCertRequest: typeof deleteCertRequest,
  downloadCertRequest: typeof downloadCertRequest,
  getCertStatus: typeof getCertStatus,
  intl: IntlShape,
  showDialogPanel: typeof showDialogPanel,
};

class CertsPage extends Component<Props> {
  handleDeleteCertRequest: Function;
  handleDownloadCertRequest: Function;
  handleShowCreateCertRequestDialog: Function;
  handleShowUploadCertDialog: Function;
  renderStatus: Function;

  constructor() {
    super();
    this.handleDeleteCertRequest = this.handleDeleteCertRequest.bind(this);
    this.handleDownloadCertRequest = this.handleDownloadCertRequest.bind(this);
    this.handleShowCreateCertRequestDialog = this.handleShowCreateCertRequestDialog.bind(
      this
    );
    this.handleShowUploadCertDialog = this.handleShowUploadCertDialog.bind(
      this
    );
    this.renderStatus = this.renderStatus.bind(this);
  }

  componentDidMount() {
    this.props.getCertStatus();
  }

  handleDeleteCertRequest() {
    this.props.deleteCertRequest();
  }

  handleDownloadCertRequest() {
    this.props.downloadCertRequest();
  }

  handleShowCreateCertRequestDialog() {
    this.props.showDialogPanel(CREATE_CERT_REQUEST_PANEL_ID);
  }

  handleShowUploadCertDialog() {
    this.props.showDialogPanel(CERT_UPLOAD_PANEL_ID);
  }

  renderStatus() {
    const { certInfo } = this.props;
    const msg = getCertStatusText(certInfo);
    const style = getCertStatusStyle(certInfo);
    return (
      <div>
        <div className="title is-4">
          <FormattedMessage {...localizedStrings.STATUS} />
        </div>
        <div
          className={`subtitle ${style}`}
          data-test-name="cert-status-message"
        >
          <FormattedMessage {...msg} />
        </div>
      </div>
    );
  }

  render() {
    const {
      activePanelId,
      certInfo,
      certRequest,
      intl: { formatDate, formatMessage },
    } = this.props;
    return (
      <div className="page" data-test-name="cert-page">
        <div className="page-header">
          <FormattedMessage {...localizedStrings.CERTS_TITLE} />
          <div>
            <button
              className="button is-primary is-inverted is-outlined"
              data-test-name="cert-create-request-button"
              onClick={this.handleShowCreateCertRequestDialog}
            >
              <span className="icon">
                <i className="fa fa-plus-circle" />
              </span>
              <FormattedMessage {...localizedStrings.CERT_CREATE_REQUEST} />
            </button>
            &nbsp;&nbsp;
            <button
              className="button is-primary is-inverted is-outlined"
              data-test-name="cert-import-button"
              onClick={this.handleShowUploadCertDialog}
            >
              <span className="icon">
                <i className="fa fa-upload" />
              </span>
              <FormattedMessage {...localizedStrings.CERT_IMPORT} />
            </button>
          </div>
        </div>
        <div className="page-contents">
          <div className="portfolio-form-container is-textured">
            <div className="box">
              {this.renderStatus()}
              {certInfo && (
                <form>
                  <div className="vertical-spacer-large" />
                  <StaticTextField
                    dataTestName="cert-info-owner"
                    label={formatMessage(localizedStrings.CERT_OWNER)}
                    value={certInfo.owner}
                  />
                  <StaticTextField
                    dataTestName="cert-info-alt-names"
                    label={formatMessage(
                      localizedStrings.SUBJECT_ALTERNATIVE_NAMES
                    )}
                    value={
                      certInfo.subjectAlternativeNames &&
                      certInfo.subjectAlternativeNames.toString()
                    }
                  />
                  <StaticTextField
                    dataTestName="cert-info-issuer"
                    label={formatMessage(localizedStrings.CERT_ISSUER)}
                    value={certInfo.issuer}
                  />
                  <StaticTextField
                    dataTestName="cert-info-expiration"
                    label={formatMessage(localizedStrings.CERT_EXPIRATION_DATE)}
                    value={formatDate(certInfo.endDate)}
                  />
                </form>
              )}
            </div>
            {certRequest && (
              <div className="box">
                <div className="certSectionHeader">
                  <div className="title is-4">
                    <FormattedMessage
                      {...localizedStrings.CERT_PENDING_REQUEST}
                    />
                  </div>
                  <div>
                    <button
                      className="button is-dark is-outlined"
                      data-test-name="cert-request-delete-button"
                      onClick={this.handleDeleteCertRequest}
                    >
                      <span className="icon">
                        <i className="fa fa-minus-circle" />
                      </span>
                      <FormattedMessage {...localizedStrings.DELETE} />
                    </button>
                    &nbsp;&nbsp;
                    <button
                      className="button is-primary is-outlined"
                      data-test-name="cert-request-download-button"
                      onClick={this.handleDownloadCertRequest}
                    >
                      <span className="icon">
                        <i className="fa fa-download" />
                      </span>
                      <FormattedMessage {...localizedStrings.CERT_DOWNLOAD} />
                    </button>
                  </div>
                </div>
                <form>
                  <StaticTextField
                    dataTestName="cert-request-domain-name"
                    label={formatMessage(
                      localizedStrings.FULLY_QUALIFIED_DOMAIN_NAME
                    )}
                    value={certRequest.FQDN}
                  />
                  <StaticTextField
                    dataTestName="cert-request-org-name"
                    label={formatMessage(localizedStrings.ORGANIZATION_NAME)}
                    value={certRequest.organizationName}
                  />
                  <StaticTextField
                    dataTestName="cert-request-org-unit"
                    label={formatMessage(localizedStrings.ORGANIZATIONAL_UNIT)}
                    value={certRequest.organizationalUnit}
                  />
                  <StaticTextField
                    dataTestName="cert-request-city"
                    label={formatMessage(localizedStrings.CITY_OR_LOCALITY)}
                    value={certRequest.cityOrLocality}
                  />
                  <StaticTextField
                    dataTestName="cert-request-state"
                    label={formatMessage(localizedStrings.STATE_OR_PROVINCE)}
                    value={certRequest.stateOrProvince}
                  />
                  <StaticTextField
                    dataTestName="cert-request-country"
                    label={formatMessage(
                      localizedStrings.TWO_LETTER_COUNTRY_CODE
                    )}
                    value={certRequest.countryCode}
                  />
                  <StaticTextField
                    dataTestName="cert-request-alt-names"
                    label={formatMessage(
                      localizedStrings.SUBJECT_ALTERNATIVE_NAMES
                    )}
                    value={
                      certRequest.subjectAlternativeNames &&
                      certRequest.subjectAlternativeNames.toString()
                    }
                  />
                  <StaticTextField
                    dataTestName="cert-request-created-date"
                    label={formatMessage(localizedStrings.CREATED_ON_DATE)}
                    value={formatDate(certRequest.timestamp)}
                  />
                </form>
              </div>
            )}
          </div>
        </div>
        {activePanelId === CREATE_CERT_REQUEST_PANEL_ID && (
          <CreateCertRequestDialog />
        )}
        {activePanelId === CERT_UPLOAD_PANEL_ID && <CertUploadDialog />}
      </div>
    );
  }
}

const certsPageWrapper = connect(
  state => ({
    activePanelId: getActivePanelId(state),
    certInfo: getCertInfo(state),
    certRequest: getCertRequestData(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      deleteCertRequest,
      downloadCertRequest,
      getCertStatus,
      showDialogPanel,
    })
)(injectIntl(CertsPage));

export default certsPageWrapper;



// WEBPACK FOOTER //
// ./src/view/Pages/CertsPage/CertsPage.js